import { FieldPermission, SmartFormPermissionData, SmartFormPermissionProvider } from '@utmost.co/smart-form';
import { GetPolicyDecisionDocument } from 'providers/SmartformPermission/data/__generated__/GetPolicyDecision.graphql';
import React, { PropsWithChildren, useCallback } from 'react';
import { useStaticPermissions } from 'src/components/common/auth/Authorize';
import { PermissionName } from 'src/graphql/types';
import { useTenantCode } from 'utils/hooks/useTenantConfig';
import { useTypedQuery } from 'utils/hooks/useTypedQuery';

export const PermissionProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { data, loading, get } = useTypedQuery(GetPolicyDecisionDocument);
  const tenantCode = useTenantCode();
  const staticPermission = useStaticPermissions();

  const hasPolicy = !!data?.getPolicyDecision?.decision?.by?.policy?.uid;

  const canViewPolicyDetails = staticPermission({ permissionName: PermissionName.SettingsPermissions }).READ;
  const canViewPolicyAnalyser = staticPermission({ permissionName: PermissionName.TenantConfig }).READ;

  const goToPolicyDetails = useCallback(
    (policyUid?: string) => {
      window.open(
        `${window.location.origin}/${tenantCode}/settings/security-permissions/details/actor${policyUid ? `?policyUid=${policyUid}` : ''}`,
        '_blank'
      );
    },
    [tenantCode]
  );

  const goToPolicyVisualizer = useCallback(
    (data: SmartFormPermissionData) => {
      const state = btoa(
        JSON.stringify({
          ...data,
          permissionName: removeCustomFieldDefinitionSuffix(data.permissionName)
        })
      );
      window.open(`${window.location.origin}/${tenantCode}/settings/tenants/admin-helpers?inspect=${state}`, '_blank');
    },
    [tenantCode]
  );

  const getPolicy = useCallback(
    (resourceUid: string | undefined, permission: FieldPermission | undefined) => {
      if (permission && permission?.PERMISSION_NAME) {
        const targetPermissionName = removeCustomFieldDefinitionSuffix(permission.PERMISSION_NAME);
        if (targetPermissionName) {
          get({
            variables: {
              permissionAction: 'READ',
              permissionName: targetPermissionName,
              resourceUid: resourceUid || ''
            }
          });
        }
      }
    },
    [get]
  );
  return (
    <SmartFormPermissionProvider
      loading={loading}
      hasPolicy={hasPolicy}
      getPolicy={getPolicy}
      canViewPolicyDetails={canViewPolicyDetails}
      canViewPolicyAnalyser={canViewPolicyAnalyser}
      policyDecision={data?.getPolicyDecision}
      goToPolicyDetails={goToPolicyDetails}
      goToPolicyVisualizer={goToPolicyVisualizer}
    >
      {children}
    </SmartFormPermissionProvider>
  );
};

function removeCustomFieldDefinitionSuffix(permissionName: string | undefined) {
  return permissionName?.startsWith('CFD') ? permissionName?.split(' ')[0] : permissionName;
}
